import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Contact as ContactIcon, Mail, Pencil, Phone, RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { Dialog } from '@/components/dialog';
import { Badge } from '@/components/badge';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { EntityAvatar } from '@/components/entity-avatar';
import {
  ChannelFields,
  PHONE_LABELS,
  EMAIL_LABELS,
  type ChannelRow,
} from '@/components/channel-fields';
import { Menu, type MenuItem } from '@/components/menu';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { formatPhone } from '@/lib/format';
import { useClients } from '@/lib/clients';
import { useFormError } from '@/lib/form-error';
import {
  useContactsDirectory,
  useDeleteDirectoryContact,
  useUnblockContact,
  useUpdateDirectoryContact,
  KIND_LABELS,
  kindTone,
  type ContactKind,
} from '@/lib/contacts-directory';
import type { ContactData } from '@/types/api';

type Filter = '' | ContactKind | 'blocked';

export function ContactsIndex() {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('');
  const [page, setPage] = useState(1);
  const viewingBlocked = filter === 'blocked';

  const { data: paginated, isLoading } = useContactsDirectory({
    q: q || undefined,
    kind: viewingBlocked ? undefined : (filter || undefined) as ContactKind | undefined,
    filter: viewingBlocked ? 'blocked' : undefined,
    page,
  });
  const rows = paginated?.data;
  const meta = paginated?.meta;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5">
      <PageHeader
        icon={ContactIcon}
        title="Contacts"
        subtitle="Everyone the business talks to: client staff, prospects, vendors, and personal contacts."
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by name, phone, or email…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value as Filter);
            setPage(1);
          }}
          className="max-w-[180px]"
        >
          <option value="">All contacts</option>
          <option value="client">Clients</option>
          <option value="vendor">Vendors</option>
          <option value="personal">Personal</option>
          <option value="other">Other</option>
          <option value="blocked">Blocked</option>
        </Select>
      </div>

      {isLoading ? (
        <SkeletonList rows={6} />
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          icon={ContactIcon}
          title={viewingBlocked ? 'No blocked numbers' : 'No contacts yet'}
          description={
            viewingBlocked
              ? 'Numbers you block from the inbox will show up here.'
              : 'Contacts appear as you identify inbound numbers and add people to clients.'
          }
        />
      ) : (
        <div className="space-y-4">
          <DataList>
            {rows.map((c) => (
              <ContactRow key={c.id} contact={c} blocked={viewingBlocked} />
            ))}
          </DataList>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function ContactRow({ contact: c, blocked }: { contact: ContactData; blocked: boolean }) {
  const unblock = useUnblockContact();
  const del = useDeleteDirectoryContact();
  const confirm = useConfirm();
  const toast = useToast();
  const [editing, setEditing] = useState(false);

  // The row's own button already offers whichever of Edit/Unblock applies, so
  // the menu carries only what it doesn't.
  const menuItems: MenuItem[] = [
    ...(blocked
      ? [{ key: 'edit', label: 'Edit', icon: Pencil, onSelect: () => setEditing(true) }]
      : []),
    {
      key: 'delete',
      label: 'Delete contact',
      icon: Trash2,
      danger: true,
      // A portal user signs in through their contact record, so the server
      // refuses this one. Say why here rather than letting the request fail.
      disabled: c.has_portal_access,
      onSelect: async () => {
        const ok = await confirm({
          title: `Delete ${c.name}?`,
          body: blocked
            ? 'Their calls and texts stay in the inbox, listed under the bare number again. This number will no longer be blocked, so a new call from it rings through.'
            : 'Their calls, texts and payments stay on file, listed under the bare number again rather than their name.',
          confirmLabel: 'Delete contact',
          tone: 'danger',
        });
        if (ok) {
          await del.mutateAsync(c.id);
          toast.success(`${c.name} deleted`);
        }
      },
    },
  ];

  return (
    <DataListRow interactive={false}>
      <EntityAvatar name={c.name} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/contacts/${c.id}`}
            className="truncate font-medium text-foreground hover:text-primary hover:underline"
          >
            {c.name}
          </Link>
          <Badge tone={kindTone(c.kind)}>{KIND_LABELS[c.kind] ?? c.kind}</Badge>
          {c.client_id ? (
            <Link
              to={`/clients/${c.client_id}`}
              className="truncate text-xs text-muted-foreground hover:text-foreground"
            >
              client
            </Link>
          ) : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {c.primary_phone ? (
            <span className="inline-flex items-center gap-1">
              <Phone className="size-3" /> {formatPhone(c.primary_phone)}
            </span>
          ) : null}
          {c.primary_email ? (
            <span className="inline-flex items-center gap-1">
              <Mail className="size-3" /> {c.primary_email}
            </span>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {blocked ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={unblock.isPending}
            onClick={async () => {
              await unblock.mutateAsync(c.id);
              toast.success(`${c.name} unblocked`);
            }}
          >
            <RotateCcw className="size-3.5" /> Unblock
          </Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            <Pencil className="size-3.5" /> Edit
          </Button>
        )}
        <Menu items={menuItems} />
      </div>

      {editing ? <EditContactDialog contact={c} onClose={() => setEditing(false)} /> : null}
    </DataListRow>
  );
}

export function EditContactDialog({ contact, onClose }: { contact: ContactData; onClose: () => void }) {
  const update = useUpdateDirectoryContact();
  const toast = useToast();
  const { error, wrap } = useFormError();
  const { data: clientsPage } = useClients();
  const clients = clientsPage?.data ?? [];

  const [name, setName] = useState(contact.name);
  const [kind, setKind] = useState<ContactKind>(contact.kind as ContactKind);
  const [clientId, setClientId] = useState<number | null>(contact.client_id);
  const [phones, setPhones] = useState<ChannelRow[]>(
    contact.phones.map((p) => ({ label: p.label, value: p.number, is_primary: p.is_primary })),
  );
  const [emails, setEmails] = useState<ChannelRow[]>(
    contact.emails.map((e) => ({ label: e.label, value: e.address, is_primary: e.is_primary })),
  );
  const [notes, setNotes] = useState(contact.notes ?? '');
  const [pending, setPending] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    const ok = await wrap(() =>
      update.mutateAsync({
        id: contact.id,
        name,
        kind,
        client_id: kind === 'client' ? (clientId ?? clients[0]?.id ?? null) : null,
        notes: notes || undefined,
        phones: phones
          .filter((p) => p.value.trim())
          .map((p) => ({ label: p.label, number: p.value.trim(), is_primary: p.is_primary })),
        emails: emails
          .filter((e) => e.value.trim())
          .map((e) => ({ label: e.label, address: e.value.trim(), is_primary: e.is_primary })),
      }),
    );
    setPending(false);
    if (ok) {
      toast.success('Contact updated');
      onClose();
    }
  }

  return (
    <Dialog open={true} onClose={onClose} title="Edit contact">
      <form className="space-y-3" onSubmit={handleSubmit}>
        <Input required placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />

        <label className="block space-y-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Type
          </span>
          <Select value={kind} onChange={(e) => setKind(e.target.value as ContactKind)}>
            <option value="client">Client</option>
            <option value="vendor">Vendor</option>
            <option value="personal">Personal</option>
            <option value="other">Other</option>
          </Select>
        </label>

        {kind === 'client' && clients.length > 0 ? (
          <label className="block space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Client
            </span>
            <Select
              value={clientId ?? clients[0]?.id ?? ''}
              onChange={(e) => setClientId(Number(e.target.value))}
            >
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          </label>
        ) : null}

        <ChannelFields
          heading="Phone numbers"
          type="phone"
          rows={phones}
          setRows={setPhones}
          labels={PHONE_LABELS}
        />
        <ChannelFields
          heading="Email addresses"
          type="email"
          rows={emails}
          setRows={setEmails}
          labels={EMAIL_LABELS}
        />

        <Textarea
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
